// @flow
import {ofType, StateObservable} from 'redux-observable';
import update from 'immutability-helper';
import {forkJoin, of, from} from 'rxjs';
import {
  map,
  switchMap,
  catchError,
  debounceTime,
  toArray,
  concatMap,
  mergeMap,
} from 'rxjs/operators';
import {buildRoute} from '../../http';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {
  createMultiFieldFormData,
  createSingleFieldFormData,
} from '../../utils/form';
import {DeleteCategorySuccess} from '../categories/duck';
import {uploadImageStoreApi} from '../file-storage/api';
import {
  addProductImageApi,
  createProductApi,
  deleteProductApi,
  editProductApi,
  endpoints,
  fetchProductApi,
  saveProductVariantsApi,
  editProductVariantApi,
  getProductsCSVApi,
  batchUpdateProductsApi,
  batchCreateProductsApi,
  getFbFeedTokenApi,
  getProductsCatalogueApi,
  addProductsCatalogueApi,
  getProductMetadataSettingApi,
  getProductSpecificationsApi,
  getRelatedProductsSettingApi,
  getProductSizeChartsApi,
  getProductVariantOptionsApi,
  saveProductSectionsApi,
  getProductsApi,
} from './api';

import type {RootState} from '../index';
import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import type {
  Product,
  ProductDetail,
  StoredFile,
  Pagination,
  ProductMetaDataSetting,
  ProductSpecificationSetting,
  RelatedProductsSetting,
  ProductSizeChartSetting,
  ProductImage,
  ProductVariantOption,
} from '../../entities';
import type {
  ProductRequest,
  ProductVariantsRequest,
  EditProductVariantRequest,
  ProductSectionRequest,
} from './api';
import type {ApiError} from '../../http/error';
import {isFeatureInTier} from '../../utils/component';

export type ProductsAction = Action & {
  productId?: string,
  product?: ProductDetail,
  products?: Product[],
  pagination?: Pagination,
  productRequest?: ProductRequest,
  productVariantsRequest?: ProductVariantsRequest,
  editProductVariantRequest?: EditProductVariantRequest,
  queryFilter?: {[key: string]: string},
  error?: string | ApiError,
  file?: File,
  templateSelected?: string,
  response?: Object,
};

export type BatchUpdateResponseLine = {
  scope: 'PRODUCTS' | 'PRODUCT_VARIANTS' | 'BULK_PRICES',
  index: number,
  message: string,
  field?: string,
  type?: 'not_found' | 'type' | 'missing',
};

export type ProductsCatalogue = {
  fileId?: string,
  state: 'BUILT' | 'PENDING',
  type: 'store-product-catalogue-file',
};

export type ProductsState = {
  isFetching?: boolean,
  isSaving?: boolean,
  products?: Product[],
  isFetchingFeatured?: boolean,
  featuredProducts?: Product[],
  fetchFeaturedProductsError?: string,
  product?: ProductDetail,
  productMetadata?: ProductMetaDataSetting,
  productSizeChart?: ProductSizeChartSetting,
  productSpecification?: ProductSpecificationSetting,
  relatedProducts?: RelatedProductsSetting,
  pagination?: Pagination,
  fetchProductsError?: string,
  fetchProductError?: string,
  saveProductError?: string,
  createProductSuccess?: string | boolean,
  editProductSuccess?: boolean,
  createProductError?: ApiError,
  editProductError?: ApiError,
  deleteProductSuccess?: boolean,
  deleteProductError?: string,
  enableProductSuccess?: boolean,
  disableProductSuccess?: boolean,
  productFeaturedSuccess?: boolean,
  productUnfeaturedSuccess?: boolean,
  stockChangeSuccess?: boolean,
  enableProductError?: string,
  queryFilter?: {[key: string]: string},
  exportedData?: File,
  isExportingProducts?: boolean,
  exportProductsSuccess?: boolean,
  exportProductsError?: boolean,
  isBatchUpdatingProducts?: boolean,
  batchUpdateProductsSuccess?: BatchUpdateResponseLine[],
  batchUpdateProductsError?: ApiError,
  fbFeedToken?: string,
  fetchFbFeedTokenSuccess?: boolean,
  fetchFbFeedTokenError?: boolean,
  createFinalSettingProductSuccess?: boolean,
  isFetchingCatalogue?: boolean,
  catalogue?: ProductsCatalogue,
  fetchCatalogueError?: string,
  isCreatingCatalogue?: boolean,
  createCatalogueSuccess?: boolean,
  createCatalogueError?: string,
  globalProductVariantOptions?: ProductVariantOption[],
};

// Fetch product actions
export class FetchProducts extends ActionCreator<ProductsAction> {}
export class FetchProductsSuccess extends ActionCreator<ProductsAction> {}
export class FetchProductsError extends ActionCreator<ProductsAction> {}
export class FetchFeaturedProducts extends ActionCreator<ProductsAction> {}
export class FetchFeaturedProductsSuccess extends ActionCreator<ProductsAction> {}
export class FetchFeaturedProductsError extends ActionCreator<ProductsAction> {}
// Fetch product actions
export class FetchProduct extends ActionCreator<ProductsAction> {}
export class FetchProductSuccess extends ActionCreator<ProductsAction> {}
export class FetchProductError extends ActionCreator<ProductsAction> {}
// Create product actions
export class CreateProduct extends ActionCreator<ProductsAction> {}
export class CreateProductSuccess extends ActionCreator<ProductsAction> {}
export class CreateProductError extends ActionCreator<ProductsAction> {}
// Add image product actions
export class AddImageProductSuccess extends ActionCreator<ProductsAction> {}
export class AddImageProductError extends ActionCreator<ProductsAction> {}
// Edit product actions
export class EditProduct extends ActionCreator<ProductsAction> {}
export class EditProductSuccess extends ActionCreator<ProductsAction> {}
export class EditProductError extends ActionCreator<ProductsAction> {}
// Product Variants actions
export class SaveProductVariants extends ActionCreator<ProductsAction> {}
export class SaveProductVariantsSuccess extends ActionCreator<ProductsAction> {}
export class SaveProductVariantsError extends ActionCreator<ProductsAction> {}
export class EditProductVariant extends ActionCreator<ProductsAction> {}
export class EditProductVariantSuccess extends ActionCreator<ProductsAction> {}
export class EditProductVariantError extends ActionCreator<ProductsAction> {}
// Enable product actions
export class EnableProduct extends ActionCreator<ProductsAction> {}
export class EnableProductSuccess extends ActionCreator<ProductsAction> {}
export class EnableProductError extends ActionCreator<ProductsAction> {}
// Delete product actions
export class DeleteProduct extends ActionCreator<ProductsAction> {}
export class DeleteProductSuccess extends ActionCreator<ProductsAction> {}
export class DeleteProductError extends ActionCreator<ProductsAction> {}

// Batch product actions
export class ExportProducts extends ActionCreator<ProductsAction> {}
export class ExportProductsSuccess extends ActionCreator<ProductsAction> {}
export class ExportProductsError extends ActionCreator<ProductsAction> {}

export class BatchUpdateProducts extends ActionCreator<ProductsAction> {}
export class BatchUpdateProductsSuccess extends ActionCreator<ProductsAction> {}
export class BatchUpdateProductsError extends ActionCreator<ProductsAction> {}

export class BatchCreateProducts extends ActionCreator<ProductsAction> {}
export class BatchCreateProductsSuccess extends ActionCreator<ProductsAction> {}
export class BatchCreateProductsError extends ActionCreator<ProductsAction> {}

export class FetchFbFeedToken extends ActionCreator<ProductsAction> {}
export class FetchFbFeedTokenSuccess extends ActionCreator<ProductsAction> {}
export class FetchFbFeedTokenError extends ActionCreator<ProductsAction> {}

export class CreateProductsCatalogue extends ActionCreator<ProductsAction> {}
export class CreateProductsCatalogueSuccess extends ActionCreator<ProductsAction> {}
export class CreateProductsCatalogueError extends ActionCreator<ProductsAction> {}

export class FetchProductsCatalogue extends ActionCreator<ProductsAction> {}
export class FetchProductsCatalogueSuccess extends ActionCreator<ProductsAction> {}
export class FetchProductsCatalogueError extends ActionCreator<ProductsAction> {}

export class FetchProductMetadata extends ActionCreator<ProductsAction> {}
export class FetchProductMetadataSuccess extends ActionCreator<ProductsAction> {}
export class FetchProductMetadataError extends ActionCreator<ProductsAction> {}

export class FetchProductSpecifications extends ActionCreator<ProductsAction> {}
export class FetchProductSpecificationsSuccess extends ActionCreator<ProductsAction> {}
export class FetchProductSpecificationsError extends ActionCreator<ProductsAction> {}

export class FetchRelatedProducts extends ActionCreator<ProductsAction> {}
export class FetchRelatedProductsSuccess extends ActionCreator<ProductsAction> {}
export class FetchRelatedProductsError extends ActionCreator<ProductsAction> {}

export class FetchProductSizeChart extends ActionCreator<ProductsAction> {}
export class FetchProductSizeChartSuccess extends ActionCreator<ProductsAction> {}
export class FetchProductSizeChartError extends ActionCreator<ProductsAction> {}

export class FetchProductVariantOptions extends ActionCreator<ProductsAction> {}
export class FetchProductVariantOptionsSuccess extends ActionCreator<ProductsAction> {}
export class FetchProductVariantOptionsError extends ActionCreator<ProductsAction> {}

export class SaveProductSection extends ActionCreator<ProductsAction> {}
export class SaveProductSectionSuccess extends ActionCreator<ProductsAction> {}
export class SaveProductSectionError extends ActionCreator<ProductsAction> {}

export class EditProductSection extends ActionCreator<ProductsAction> {}
export class EditProductSectionSuccess extends ActionCreator<ProductsAction> {}
export class EditProductSectionError extends ActionCreator<ProductsAction> {}

export class SaveQueryFilter extends ActionCreator<ProductsAction> {}

export class ResetBatchUpload extends ActionCreator<ProductsAction> {}

export class ResetPartialState extends ActionCreator<ProductsAction> {}
export class ResetSelectedProduct extends ActionCreator<ProductsAction> {}
export class ResetProductSettings extends ActionCreator<ProductsAction> {}
export class ResetSpecificationSettings extends ActionCreator<ProductsAction> {}
export class ResetRelatedProducts extends ActionCreator<ProductsAction> {}

export const actions = {
  fetchProducts: (payload: ProductsAction) => new FetchProducts(payload),
  fetchFeaturedProducts: (payload: ProductsAction) =>
    new FetchFeaturedProducts(payload),
  fetchProduct: (payload: ProductsAction) => new FetchProduct(payload),
  createProduct: (payload: ProductsAction) => new CreateProduct(payload),
  editProduct: (payload: ProductsAction) => new EditProduct(payload),
  enableProduct: (payload: ProductsAction) => new EnableProduct(payload),
  deleteProduct: (payload: ProductsAction) => new DeleteProduct(payload),
  saveQueryFilter: (payload: ProductsAction) => new SaveQueryFilter(payload),
  editProductVariant: (payload: ProductsAction) =>
    new EditProductVariant(payload),
  exportProducts: (payload: ProductsAction) => new ExportProducts(payload),
  batchUpdateProducts: (payload: ProductsAction) =>
    new BatchUpdateProducts(payload),
  batchCreateProducts: (payload: ProductsAction) =>
    new BatchCreateProducts(payload),
  fetchFbFeedToken: (payload: ProductsAction) => new FetchFbFeedToken(payload),
  createProductsCatalogue: () => new CreateProductsCatalogue(),
  fetchProductsCatalogue: () => new FetchProductsCatalogue(),
  resetState: () => new ResetPartialState(),
  resetProductState: () => new ResetSelectedProduct(),
  resetProductSettings: () => new ResetProductSettings(),
  resetBatchUpload: () => new ResetBatchUpload(),

  resetSpecificationSettings: () => new ResetSpecificationSettings(),
  resetRelatedProducts: () => new ResetRelatedProducts(),

  fetchProductMetadata: (payload: ProductsAction) =>
    new FetchProductMetadata(payload),
  fetchProductSpecifications: (payload: ProductsAction) =>
    new FetchProductSpecifications(payload),
  fetchRelatedProducts: (payload: ProductsAction) =>
    new FetchRelatedProducts(payload),
  fetchProductSizeChart: (payload: ProductsAction) =>
    new FetchProductSizeChart(payload),
  fetchProductVariantOptions: () => new FetchProductVariantOptions(),
};

export const initialState: ProductsState = {};

export default createReducer(initialState, {
  // Fetching products
  [FetchProducts.type]: (state: ProductsState, action: ProductsAction) => ({
    ...state,
    isFetching: true,
    products: undefined,
    fetchProductsError: undefined,
    modalOpen: false,
  }),
  [FetchProductsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetching: false,
    products: action.products,
    pagination: action.pagination,
    queryFilter: action.queryFilter,
  }),
  [FetchProductsError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetching: false,
    fetchProductsError: action.error,
  }),
  [FetchFeaturedProducts.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetchingFeatured: true,
    fetchFeaturedProductsError: undefined,
  }),
  [FetchFeaturedProductsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetchingFeatured: undefined,
    featuredProducts: action.products,
  }),
  [FetchFeaturedProductsError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetchingFeatured: undefined,
    fetchFeaturedProductsError: action.error,
  }),
  // Fetching one product
  [FetchProduct.type]: (state: ProductsState, action: ProductsAction) => ({
    ...state,
    isFetching: true,
    // productMetadata: undefined,
    // productSpecifications: undefined,
    fetchProductError: undefined,
  }),
  [FetchProductSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetching: false,
    product: action.product,
  }),
  [FetchProductError.type]: (state: ProductsState, action: ProductsAction) => ({
    ...state,
    isFetching: false,
    fetchProductError: action.error,
  }),
  [FetchProductMetadataSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    productMetadata: action.data,
  }),
  [FetchProductSizeChartSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    productSizeChart: action.data,
  }),
  [FetchProductMetadataError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    productMetadata: undefined,
  }),
  [FetchRelatedProductsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    relatedProducts: action.data,
  }),
  [ResetRelatedProducts.type]: (state: ProductsState) => ({
    ...state,
    relatedProducts: undefined,
  }),
  [FetchRelatedProductsError.type]: (state, action: ProductsAction) => ({
    ...state,
    relatedProducts: undefined,
  }),
  [FetchProductSpecificationsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    productSpecification: action.data,
  }),
  [FetchProductSpecificationsError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    productSpecification: undefined,
  }),
  [ResetSpecificationSettings.type]: (state: ProductsState) => ({
    ...state,
    productSpecification: undefined,
  }),
  // Create product
  [CreateProduct.type]: (state: ProductsState) => ({
    ...state,
    isSaving: true,
    createProductSuccess: undefined,
    createProductError: undefined,
  }),
  [AddImageProductSuccess.type]: (state: ProductsState) => ({
    ...state,
    isSaving: false,
    // isFetching: true,
    createProductSuccess: true,
    createProductError: undefined,
  }),
  [CreateProductSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    // isSaving: false,
    createProductError: undefined,
  }),
  [CreateProductError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isSaving: false,
    createProductSuccess: false,
    createProductError: action.error,
  }),
  // Edit product
  [EditProduct.type]: (state: ProductsState) => ({
    ...state,
    isSaving: true,
    editProductSuccess: undefined,
    saveProductError: undefined,
  }),
  [EditProductSuccess.type]: (state: ProductsState) => ({
    ...state,
    isSaving: false,
    editProductSuccess: true,
    saveProductError: undefined,
  }),
  [EditProductError.type]: (state: ProductsState, action: ProductsAction) => ({
    ...state,
    isSaving: false,
    editProductSuccess: false,
    saveProductError: action.error,
  }),
  // ProductVariants
  [SaveProductVariants.type]: (state: ProductsState) => ({
    ...state,
    isSaving: true,
    // saveProductError: undefined,
  }),
  [SaveProductVariantsSuccess.type]: (state: ProductsState) => ({
    ...state,
    isSaving: false,
    createFinalSettingProductSuccess: true,
    // isFetching: true,
    // saveProductError: undefined,
  }),
  [SaveProductVariantsError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isSaving: false,
    // saveProductError: action.error,
  }),
  [EditProductVariant.type]: (state: ProductsState) => ({
    ...state,
    isSaving: true,
    editProductVariantSuccess: undefined,
    editProductVariantError: undefined,
    stockChangeSuccess: undefined,
  }),
  [EditProductVariantSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isSaving: false,
    editProductVariantSuccess: true,
    editProductVariantError: undefined,
    stockChangeSuccess: action.stock != null ? true : undefined,
  }),
  [EditProductVariantError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isSaving: false,
    editProductVariantSuccess: false,
    editProductVariantError: action.error,
  }),
  // Enable product
  [EnableProduct.type]: (state: ProductsState, action: ProductsAction) => {
    let result = {
      ...state,
      isSaving: true,
      enableProductSuccess: undefined,
      enableProductError: undefined,
      productFeaturedSuccess: undefined,
      productUnfeaturedSuccess: undefined,
      stockChangeSuccess: undefined,
      saveProductError: undefined,
    };
    let products;
    let featuredProducts;
    const {productRequest} = action;
    if (
      (typeof productRequest.featured != 'undefined' ||
        typeof productRequest.enabled != 'undefined') &&
      state.products
    ) {
      products = state.products.map((product: Product, index: number) => ({
        ...product,
        featured:
          typeof productRequest.featured != 'undefined' &&
          product.id === productRequest.id
            ? productRequest.featured
            : product.featured,
        enabled:
          typeof productRequest.enabled != 'undefined' &&
          product.id === productRequest.id
            ? productRequest.enabled
            : product.enabled,
      }));
      result.products = products;
    }
    if (typeof productRequest.featured != 'undefined') {
      if (!productRequest.featured && state.featuredProducts) {
        featuredProducts = state.featuredProducts.filter(
          p => p.id !== productRequest.id
        );
      }
      if (productRequest.featured && state.products) {
        let index = state.products.findIndex(p => p.id === productRequest.id);
        if (index !== -1 && state.featuredProducts && state.products)
          featuredProducts = [state.products[index], ...state.featuredProducts];
      }
      result.featuredProducts = featuredProducts;
    }
    return result;
  },
  [EnableProductSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => {
    const aux = [...(state.products || [])];
    const index = aux.findIndex(p => p.id === action.product.id);
    console.log({aux, index});
    if (index !== -1) {
      aux[index] = {
        ...aux[index],
        ...action.product,
      };
    }
    return {
      ...state,
      products: aux,
      isSaving: false,
      // isFetching: true,
      enableProductSuccess:
        typeof action.enabled === 'boolean' ? !!action.enabled : undefined,
      disableProductSuccess:
        typeof action.enabled === 'boolean' ? !action.enabled : undefined,
      productFeaturedSuccess:
        typeof action.featured === 'boolean' ? !!action.featured : undefined,
      productUnfeaturedSuccess:
        typeof action.featured === 'boolean' ? !action.featured : undefined,
      stockChangeSuccess: action.stock != null ? true : undefined,
      enableProductError: undefined,
      saveProductError: undefined,
    };
  },
  [EnableProductError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isSaving: false,
    enableProductError: action.error,
    // featureProductError: action.error,
    // saveProductError: action.error,
  }),
  // Delete product
  [DeleteProduct.type]: (state: ProductsState) => ({
    ...state,
    isSaving: true,
    deleteProductSuccess: undefined,
    deleteProductError: undefined,
  }),
  [DeleteProductSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    products: state.products
      ? state.products.filter(p => p.id !== action.id)
      : undefined,
    featuredProducts: state.featuredProducts
      ? state.featuredProducts.filter(p => p.id !== action.id)
      : undefined,
    isSaving: false,
    isFetching: true,
    deleteProductSuccess: true,
    deleteProductError: undefined,
  }),
  [DeleteProductError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isSaving: false,
    deleteProductError: action.error,
  }),
  // batch products
  [ExportProducts.type]: (state: ProductsState) => ({
    ...state,
    exportedData: undefined,
    isExportingProducts: true,
    exportProductsSuccess: undefined,
    exportProductsError: undefined,
  }),
  [ExportProductsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    exportedData: action.data,
    isExportingProducts: false,
    exportProductsSuccess: true,
  }),
  [ExportProductsError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isExportingProducts: false,
    exportProductsSuccess: false,
    exportProductsError: action.error,
  }),
  // Edit product
  [BatchUpdateProducts.type]: (state: ProductsState) => ({
    ...state,
    isBatchUpdatingProducts: true,
    batchUpdateProductsSuccess: undefined,
    batchUpdateProductsError: undefined,
  }),
  [BatchUpdateProductsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isBatchUpdatingProducts: false,
    batchUpdateProductsSuccess: action.response,
  }),
  [BatchUpdateProductsError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isBatchUpdatingProducts: undefined,
    batchUpdateProductsError: action.error,
  }),
  [BatchCreateProducts.type]: (state: ProductsState) => ({
    ...state,
    isBatchUpdatingProducts: true,
    batchUpdateProductsSuccess: undefined,
    batchUpdateProductsError: undefined,
  }),
  [BatchCreateProductsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isBatchUpdatingProducts: false,
    batchUpdateProductsSuccess: action.response,
  }),
  [BatchCreateProductsError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isBatchUpdatingProducts: undefined,
    batchUpdateProductsError: action.error,
  }),
  [ResetBatchUpload.type]: (state: ProductsState, action: ProductsAction) => ({
    ...state,
    isBatchUpdatingProducts: undefined,
    batchUpdateProductsSuccess: undefined,
    batchUpdateProductsError: undefined,
  }),
  [FetchFbFeedToken.type]: (state: ProductsState, action: ProductsAction) => ({
    ...state,
    // isFetching: true,
    fbFeedToken: undefined,
    fetchFbFeedTokenSuccess: undefined,
    fetchFbFeedTokenError: undefined,
  }),
  [FetchFbFeedTokenSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    fetchFbFeedTokenSuccess: true,
    // isFetching: false,
    fbFeedToken: action.token,
  }),
  [FetchFbFeedTokenError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    // isFetching: false,
    fetchFbFeedTokenError: action.error,
  }),
  [SaveQueryFilter.type]: (state: ProductsState, action: ProductsAction) => ({
    ...state,
    queryFilter: action.queryFilter,
  }),
  [ResetPartialState.type]: (state: ProductsState) => ({
    ...initialState,
    products: state.products,
    pagination: state.pagination,
    featuredProducts: state.featuredProducts,
  }),
  [ResetSelectedProduct.type]: (state: ProductsState) => ({
    ...state,
    product: undefined,
    isFetching: undefined,
    isSaving: undefined,
    productOperationError: undefined,
    fetchProductError: undefined,
    createProductSuccess: undefined,
    editProductSuccess: undefined,
    deleteProductSuccess: undefined,
    createFinalSettingProductSuccess: undefined,
    stockChangeSuccess: undefined,
    enableProductSuccess: undefined,
    enableProductError: undefined,
    productFeaturedSuccess: undefined,
    productUnfeaturedSuccess: undefined,
  }),
  [ResetProductSettings.type]: (state: ProductsState) => ({
    ...state,
    productMetadata: undefined,
    productSpecification: undefined,
    relatedProducts: undefined,
    productSizeChart: undefined,
  }),
  [FetchProductsCatalogue.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetchingCatalogue: true,
    createCatalogueSuccess: undefined,
    createCatalogueError: undefined,
  }),
  [FetchProductsCatalogueSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetchingCatalogue: false,
    fetchCatalogueSuccess: true,
    fetchCatalogueError: undefined,
    catalogue: action.catalogue,
  }),
  [FetchProductsCatalogueError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isFetchingCatalogue: false,
    catalogue: undefined,
    fetchCatalogueError: action.error,
    fetchCatalogueSuccess: true,
  }),
  [CreateProductsCatalogue.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    catalogue: undefined,
    isCreatingCatalogue: true,
    createCatalogueSuccess: undefined,
    createCatalogueError: undefined,
  }),
  [CreateProductsCatalogueSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isCreatingCatalogue: undefined,
    createCatalogueSuccess: true,
    createCatalogueError: undefined,
  }),
  [CreateProductsCatalogueError.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    isCreatingCatalogue: undefined,
    createCatalogueSuccess: undefined,
    createCatalogueError: action.error,
  }),
  [FetchProductVariantOptionsSuccess.type]: (
    state: ProductsState,
    action: ProductsAction
  ) => ({
    ...state,
    globalProductVariantOptions: action.data,
  }),
  // [SaveProductSectionSuccess.type]: (
  //   state: ProductsState,
  //   action: ProductsAction
  // ) => ({
  //   ...state,
  //   isSaving: false,
  //   editProductSuccess: true,
  // }),
  // [SaveProductSectionError.type]: (
  //   state: ProductsState,
  //   action: ProductsAction
  // ) => ({
  //   ...state,
  //   isSaving: false,
  // }),
});

export function fetchProductsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      FetchProducts.type,
      AddImageProductSuccess.type,
      SaveProductVariantsSuccess,
      EditProductSuccess.type,
      // EditCategorySuccess.type,
      DeleteCategorySuccess.type,
      EditProductVariantSuccess.type,
      EnableProductError.type,
      BatchCreateProductsSuccess.type
    ),
    debounceTime(250),
    switchMap(({queryFilter, type}: ProductsAction) => {
      const prevQueryFilter = state$.value?.products?.queryFilter;
      const qf = type === FetchProducts.type ? queryFilter : prevQueryFilter;

      const difName = queryFilter?.name !== prevQueryFilter?.name;
      const difCategory =
        queryFilter?.categories !== prevQueryFilter?.categories;

      const resetPage = difName || difCategory;

      let updatedQueryFilter = {
        ...qf,
      };

      if (type === FetchProducts.type) {
        updatedQueryFilter = {
          ...qf,
          page: resetPage ? 0 : queryFilter?.page,
        };
      }

      if (!queryFilter?.querySize) {
        return getProductsApi({
          ...updatedQueryFilter,
        }).pipe(
          map(
            ({data, pagination}: {data: Product[], pagination: Pagination}) =>
              new FetchProductsSuccess({
                products: data,
                pagination,
                queryFilter: updatedQueryFilter,
              })
          ),
          buildCatchError(FetchProductsError, 'Error al obtener productos')
        );
      }

      console.log({updatedQueryFilter});

      return getProductsApi({
        ...updatedQueryFilter,
        product_type: 'LISTING',
      }).pipe(
        map(
          ({data, pagination}: {data: Product[], pagination: Pagination}) =>
            new FetchProductsSuccess({
              products: data,
              pagination,
              queryFilter: updatedQueryFilter,
            })
        ),
        buildCatchError(FetchProductsError, 'Error al obtener productos')
      );
    })
  );
}

export function fetchFeaturedProductsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      FetchFeaturedProducts.type,
      AddImageProductSuccess.type,
      SaveProductVariantsSuccess.type,
      EditProductSuccess.type,
      // EditCategorySuccess.type,
      DeleteCategorySuccess.type,
      EditProductVariantSuccess.type
    ),
    debounceTime(250),
    switchMap(() => {
      return getProductsApi({featured: '1', size: 20}).pipe(
        map(
          ({data}: {data: Product[]}) =>
            new FetchFeaturedProductsSuccess({products: data})
        ),
        buildCatchError(
          FetchFeaturedProductsError,
          'Error al obtener productos destacados'
        )
      );
    })
  );
}

export function fetchProductEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchProduct.type),
    switchMap(({productId}: ProductsAction) => {
      return fetchProductApi(undefined, {
        route: buildRoute(endpoints.FETCH_PRODUCT, [productId]),
      }).pipe(
        map((data: ProductDetail) => new FetchProductSuccess({product: data})),
        buildCatchError(FetchProductError, 'Error al obtener producto')
      );
    })
  );
}

export function createProductEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(CreateProduct.type),
    switchMap((action: ProductsAction) => {
      if (
        action.productRequest.imageFiles.length > 1 &&
        !isFeatureInTier('VARIOUS_PRODUCT_IMAGES', state$.value.store.store)
      ) {
        return of(
          new CreateProductError({error: 'No puedes agregar más de 1 imagen'})
        );
      }
      return of(action);
    }),
    mergeMap(
      ({
        productRequest,
        productVariantsRequest,
        productSectionsRequest,
      }: ProductsAction) => {
        let request = productRequest;
        if (request.imageFiles)
          request = update(request, {$unset: ['imageFiles']});
        return createProductApi(request).pipe(
          mergeMap((product: ProductDetail) => {
            const actions = [
              new CreateProductSuccess({
                payload: [
                  product,
                  productRequest.imageFiles,
                  {
                    id: product.id,
                  },
                  productVariantsRequest,
                ],
              }),
            ];

            if (productSectionsRequest) {
              actions.push(
                new SaveProductSection({
                  productSectionsRequest,
                  productId: product.id,
                })
              );
            }

            return actions;
          }),
          buildCatchError(CreateProductError)
        );
      }
    )
  );
}

export function saveProductSectionEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveProductSection.type),
    switchMap(({productSectionsRequest, productId}) => {
      const files: File[] = [];

      for (const section of productSectionsRequest) {
        if (section.file) files.push(section.file);
      }

      if (files.length === 0) {
        return of([[], productId, productSectionsRequest, undefined]);
      }

      return uploadImageStoreApi(
        createMultiFieldFormData(files, undefined, {
          filePrefix: 'im/product',
        })
      ).pipe(
        map((files: StoredFile[]) => [
          files,
          productId,
          productSectionsRequest,
          undefined,
        ]),
        catchError((error: ApiError) =>
          of([undefined, undefined, productSectionsRequest, error])
        )
      );
    }),
    concatMap(
      ([files, productId, productSectionsRequest, pipeError]: [
        StoredFile[],
        string,
        ProductSectionRequest,
        ApiError
      ]) => {
        if (pipeError) return of(new CreateProductError({error: pipeError}));

        const idWithFiles = [];
        const rq = [];

        productSectionsRequest.forEach((el, idx) => {
          if (!!el.file) idWithFiles.push(idx);
          const {file, ...rest} = el;
          rq.push(rest);
        });

        files.forEach((f, idx) => {
          const index = idWithFiles[idx];
          rq[index].imageId = f.id;
        });

        return saveProductSectionsApi(
          {sections: rq},
          {
            route: buildRoute(endpoints.GET_PRODUCT_SECTIONS, [productId]),
          }
        ).pipe(
          map(() => new AddImageProductSuccess()),
          buildCatchError(CreateProductError, pipeError || undefined)
        );
      }
    )
  );
}

export function addProductImageEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CreateProductSuccess.type),
    switchMap(({payload}) => {
      const [
        product,
        imageFiles,
        productRequest,
        productVariantsRequest,
      ] = payload;
      return from(imageFiles).pipe(
        concatMap(file =>
          uploadImageStoreApi(
            createSingleFieldFormData(file, undefined, {
              filePrefix: 'im/product',
            })
          ).pipe(
            map((file: StoredFile[]) => file[0]),
            catchError((error: ApiError) => of({error}))
          )
        ),
        toArray(),
        switchMap(filesArray => {
          const errors = filesArray.filter(file => file.error);
          const successfulFiles = filesArray.filter(file => !file.error);

          if (errors.length > 0) {
            return of(new CreateProductError({error: errors[0].error}));
          }

          return addProductImageApi(
            {fileIds: successfulFiles.map(f => f.id)},
            {route: buildRoute(endpoints.ADD_PRODUCT_IMAGE, [product.id])}
          ).pipe(
            map(
              () =>
                new AddImageProductSuccess({
                  productRequest: productRequest,
                  productVariantsRequest: productVariantsRequest,
                  files: successfulFiles,
                })
            ),
            buildCatchError(CreateProductError)
          );
        })
      );
    })
  );
}

export function saveProductVariantsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(AddImageProductSuccess.type),
    switchMap(
      ({productRequest, productVariantsRequest, files}: ProductsAction) => {
        if (
          productVariantsRequest &&
          productVariantsRequest.variants.length > 0
        ) {
          let newProductVariantsRequest = {...productVariantsRequest};

          if (files && files.length) {
            newProductVariantsRequest.variants.forEach(v => {
              if (typeof v.imageId === 'number') {
                v.imageId = files[v.imageId].id;
              }
            });
          }

          return saveProductVariantsApi(newProductVariantsRequest, {
            route: buildRoute(endpoints.SAVE_PRODUCT_VARIANTS, [
              productRequest.id,
            ]),
          }).pipe(
            map(() => new SaveProductVariantsSuccess()),
            buildCatchError(
              SaveProductVariantsError,
              'Error al guardar variantes'
            )
            /*buildCatchError(EditProductError, 'Error al editar el producto')*/
          );
        } else return [];
      }
    )
  );
}

export function editProductSectionEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditProductSection.type),
    switchMap(({productSectionRequest, productId}) => {
      const files: File[] = [];

      for (const section of productSectionRequest) {
        if (section.file) files.push(section.file);
      }

      if (!files.length)
        return of([[], productId, productSectionRequest, undefined]);

      return uploadImageStoreApi(
        createMultiFieldFormData(files, undefined, {
          filePrefix: 'im/product-section',
        })
      ).pipe(
        map((files: StoredFile[]) => [
          files,
          productId,
          productSectionRequest,
          undefined,
        ]),
        catchError((error: ApiError) =>
          of([undefined, undefined, productSectionRequest, error])
        )
      );
    }),
    concatMap(
      ([files, productId, productSectionRequest, pipeError]: [
        StoredFile[],
        string,
        ProductSectionRequest,
        ApiError
      ]) => {
        if (pipeError) return of(new CreateProductError({error: pipeError}));

        const idWithFiles = [];
        const rq = [];

        productSectionRequest.forEach((el, idx) => {
          if (!!el.file) idWithFiles.push(idx);
          const {file, ...rest} = el;
          rq.push(rest);
        });

        files.forEach((f, idx) => {
          const index = idWithFiles[idx];
          rq[index].imageId = f.id;
        });

        return saveProductSectionsApi(
          {sections: rq},
          {
            route: buildRoute(endpoints.GET_PRODUCT_SECTIONS, [productId]),
          }
        ).pipe(
          map(() => new EditProductSuccess()),
          buildCatchError(CreateProductError, pipeError || undefined)
        );
      }
    )
  );
}

export function editProductEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditProduct.type),
    switchMap((action: ProductsAction) => {
      // const completeArrayLength =
      //   action.productRequest.imageFiles.length +
      //   action.productRequest.images.length;
      // if (
      //   completeArrayLength > 1 &&
      //   !useIsFeatureInTier(
      //     action.productRequest.storeTier,
      //     'Various product images'
      //   )
      // ) {
      //   return of([
      //     new EditProductError({error: 'No puedes agregar más de 1 imagen'}),
      //     null,
      //   ]);
      // }
      return of([undefined, action]);
    }),
    mergeMap(([error: CreateProductError, action: ProductsAction]) => {
      if (error) {
        return of([error, undefined, undefined, undefined, undefined]);
      }
      const {productRequest} = action;
      const request = update(productRequest, {
        $unset: ['imageFiles', 'id', 'images', 'storeTier'],
      });
      if (productRequest.imageFiles && productRequest.imageFiles.length) {
        return from(productRequest.imageFiles).pipe(
          concatMap(file =>
            uploadImageStoreApi(
              createSingleFieldFormData(file, undefined, {
                filePrefix: 'im/product',
              })
            ).pipe(
              map((storedFile: StoredFile) => storedFile[0]),
              catchError((error: ApiError) => of({error}))
            )
          ),
          toArray(),
          map((files: StoredFile[]) => [
            undefined,
            files,
            request,
            productRequest.id,
            productRequest.images,
          ])
        );
      } else {
        return of([
          undefined,
          undefined,
          request,
          productRequest.id,
          productRequest.images,
        ]);
      }
    }),
    switchMap(
      ([error, files, productRequest, productId, productImages]: [
        EditProductError,
        ?(StoredFile[]),
        ?ProductRequest,
        ?string,
        ProductImage[]
      ]) => {
        if (error) return of(error);
        if (!productRequest || !productId)
          return of(
            new EditProductError({error: 'Error al editar el producto'})
          );
        const tasks = [];
        let newProductRequest = {...productRequest};
        delete newProductRequest.productSectionRequest;

        const {productSectionRequest} = productRequest;
        if (
          files &&
          files.length &&
          newProductRequest.productVariantSetting &&
          newProductRequest.productVariantSetting.variants &&
          newProductRequest.productVariantSetting.variants.length > 0
        ) {
          newProductRequest.productVariantSetting.variants.forEach(v => {
            if (typeof v.imageId === 'number') {
              const file = files[v.imageId];
              if (file) v.imageId = file.id;
            }
          });
        }

        tasks.push(
          editProductApi(newProductRequest, {
            route: buildRoute(endpoints.EDIT_PRODUCT, [productId]),
          }).pipe(
            mergeMap(data => {
              const actions = [];

              actions.push(
                new EditProductSuccess({
                  productId: data.id,
                })
              );

              if (productSectionRequest) {
                actions.push(
                  new EditProductSection({productSectionRequest, productId})
                );
              }
              return actions;
            }),
            buildCatchError(EditProductError, 'Error al editar el producto')
          )
        );
        const images = [];
        if (productImages && productImages.length) {
          productImages.forEach(i => images.push(i.id));
        }
        if (files && files.length) {
          files.forEach(f => images.push(f.id));
        }
        tasks.push(
          addProductImageApi(
            {fileIds: images},
            {
              route: buildRoute(endpoints.ADD_PRODUCT_IMAGE, [productId]),
            }
          ).pipe(
            map(() => null),
            buildCatchError(EditProductError, 'Error al subir la imagen')
          )
        );
        return forkJoin(tasks).pipe(
          switchMap((dataError: ProductsAction[]) => {
            if (
              (dataError[0] && dataError[0].type === 'OpenTier22Modal') ||
              (dataError[1] && dataError[1].type === 'OpenTier22Modal')
            ) {
              return of(
                ...[
                  new EditProductError({
                    error: 'Error al editar el producto',
                  }),
                  dataError[0] || dataError[1],
                ]
              );
            }
            return of(dataError[0] || dataError[1] || new EditProductSuccess());
          })
        );
      }
    )
  );
}

export function fetchProductSizeChartsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchProductSizeChart.type),
    switchMap(({productId}: ProductsAction) =>
      getProductSizeChartsApi(undefined, {
        route: buildRoute(endpoints.GET_PRODUCT_SIZE_CHARTS, [productId]),
      }).pipe(
        map(
          (data: ProductSizeChartSetting) =>
            new FetchProductSizeChartSuccess({data})
        ),
        buildCatchError(
          FetchProductSizeChartError,
          'Error al obtener metadada.'
        )
      )
    )
  );
}

export function fetchProductMetadataSettingEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchProductMetadata.type),
    switchMap(({productId}: ProductsAction) =>
      getProductMetadataSettingApi(undefined, {
        route: buildRoute(endpoints.GET_METADATA_SETTING, [productId]),
      }).pipe(
        map(
          (data: ProductMetaDataSetting) =>
            new FetchProductMetadataSuccess({data})
        ),
        buildCatchError(FetchProductMetadataError, 'Error al obtener metadada.')
      )
    )
  );
}

export function fetchProductSpecificationsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchProductSpecifications.type),
    switchMap(({productId}: ProductsAction) =>
      getProductSpecificationsApi(undefined, {
        route: buildRoute(endpoints.GET_PRODUCT_SPECIFICATIONS, [productId]),
      }).pipe(
        map(
          (data: ProductMetaDataSetting) =>
            new FetchProductSpecificationsSuccess({data})
        ),
        buildCatchError(
          FetchProductSpecificationsError,
          'Error al obtener especificaciones del producto.'
        )
      )
    )
  );
}

export function fetchRelatedProductsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchRelatedProducts.type),
    switchMap(({productId}: ProductsAction) => {
      return getRelatedProductsSettingApi(undefined, {
        route: buildRoute(endpoints.GET_RELATED_PRODUCTS, [productId]),
      }).pipe(
        map(
          (data: RelatedProductsSetting) =>
            new FetchRelatedProductsSuccess({data})
        ),
        buildCatchError(
          FetchRelatedProductsError,
          'Error al obtener productos relacionados.'
        )
      );
    })
  );
}

// export function saveRelatedProductsEpic(action$: ActionsObservable) {
//   return action$.pipe(
//     ofType(EditProduct.type, CreateProductSuccess.type),
//     switchMap(({productRequest, relatedProductsRequest}: ProductsAction) => {
//       if (productRequest.enabledProductVariants) {
//         relatedProductsRequest.criteria.forEach(c => {
//           if (c.related[0].productId === '')
//             c.related[0].productId = productRequest.id;
//         });
//         return saveRelatedProductsApi(relatedProductsRequest, {
//           route: buildRoute(endpoints.SAVE_RELATED_PRODUCTS, [
//             productRequest.id,
//           ]),
//         }).pipe(
//           map(() => new SaveRelatedProductsSuccess()),
//           catchError(
//             error =>
//               new SaveRelatedProductsError({
//                 error: 'Error al guardar variantes',
//               })
//           )
//           /*buildCatchError(EditProductError, 'Error al editar el producto')*/
//         );
//       } else return [];
//     })
//   );
// }

/*export function editProductEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditProduct.type),
    switchMap(({productRequest}: ProductsAction) => {
      const tasks = [];
      const {id, imageFile} = productRequest;

      const partialRequest = update(productRequest, {
        $unset: ['imageFile', 'id'],
      });
      if (Object.keys(partialRequest).length) {
        tasks.push(
          editProductApi(partialRequest, {
            route: buildRoute(endpoints.EDIT_PRODUCT, [id]),
          }).pipe(
            map(() => null),
            buildCatchError(EditProductError, 'Error al actualizar los datos')
          )
        );
      }

      if (productRequest.imageFile) {
        tasks.push(
          saveProductImageApi(createSingleFieldFormData(imageFile), {
            route: buildRoute(endpoints.SAVE_PRODUCT_IMAGE, [id, '0']),
          }).pipe(
            map(() => null),
            buildCatchError(EditProductError, 'Error al subir la imagen')
          )
        );
      }
      return forkJoin(tasks).pipe(
        map(
          (dataError: EditProductError[]) =>
            dataError[0] || dataError[1] || new EditProductSuccess()
        )
      );
    })
  );
}*/

export function enableProductEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EnableProduct.type),
    debounceTime(300),
    switchMap(({productRequest}: ProductsAction) => {
      const request = update(productRequest, {$unset: ['id']});
      return editProductApi(request, {
        route: buildRoute(endpoints.EDIT_PRODUCT, [productRequest.id]),
      }).pipe(
        map(data => {
          return new EnableProductSuccess({
            product: data,
            enabled: productRequest.enabled,
            featured:
              typeof productRequest.enabled === 'undefined'
                ? productRequest.featured
                : undefined,
            stock:
              productRequest.stockSetting && productRequest.stockSetting.stock,
          });
        }),
        buildCatchError(EditProductError)
        /*buildCatchError(EditProductError, 'Error al editar el producto')*/
      );
    })
  );
}

export function editProductVariantEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditProductVariant.type),
    switchMap(({editProductVariantRequest}: ProductsAction) => {
      return editProductVariantApi(editProductVariantRequest.request, {
        route: buildRoute(endpoints.EDIT_PRODUCT_VARIANT, [
          editProductVariantRequest.productId,
          editProductVariantRequest.variantId,
        ]),
      }).pipe(
        map(
          () =>
            new EditProductVariantSuccess({
              stock: editProductVariantRequest.request.stock,
            })
        ),
        buildCatchError(EditProductVariantError, 'Error al editar el producto')
        /*buildCatchError(EditProductVariantError, 'Error al editar el producto')*/
      );
    })
  );
}

export function exportProductsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(ExportProducts.type),
    switchMap(() => {
      const qf = {asdfasfsa: 'asdadsa'};
      return getProductsCSVApi({...qf}).pipe(
        map((data: File) => {
          return new ExportProductsSuccess({file: data});
        }),
        buildCatchError(ExportProductsError, 'Error al exportar productos')
      );
    })
  );
}

export function batchUpdateProductsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(BatchUpdateProducts.type),
    switchMap(({file}: ProductsAction) => {
      return batchUpdateProductsApi(createSingleFieldFormData(file)).pipe(
        map(response => new BatchUpdateProductsSuccess({response})),
        buildCatchError(BatchUpdateProductsError)
      );
    })
  );
}

export function batchCreateProductsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(BatchCreateProducts.type),
    switchMap(({file}: ProductsAction) => {
      return batchCreateProductsApi(createSingleFieldFormData(file), {
        queryParams: {
          format: 'xlsx',
        },
      }).pipe(
        map(response => new BatchCreateProductsSuccess({response})),
        buildCatchError(BatchCreateProductsError)
      );
    })
  );
}

export function fetchFbFeedTokenEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchFbFeedToken.type),
    switchMap(() => {
      return getFbFeedTokenApi({}).pipe(
        map(
          ({verificationCode}) =>
            new FetchFbFeedTokenSuccess({token: verificationCode})
        ),
        buildCatchError(
          FetchFbFeedTokenError,
          'Error al generar tu link de integración'
        )
      );
    })
  );
}

export function deleteProductEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DeleteProduct.type),
    switchMap(({productRequest}: ProductsAction) =>
      deleteProductApi({
        route: buildRoute(endpoints.DELETE_PRODUCT, [productRequest.id]),
      }).pipe(
        map(() => new DeleteProductSuccess({id: productRequest.id})),
        buildCatchError(DeleteProductError)
      )
    )
  );
}

export function addProductsCatalogueEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CreateProductsCatalogue.type),
    switchMap(() => {
      return addProductsCatalogueApi({}).pipe(
        map(() => new CreateProductsCatalogueSuccess()),
        buildCatchError(
          CreateProductsCatalogueError,
          'No se pudo generar el catálogo'
        )
      );
    })
  );
}

export function fetchProductsCatalogueEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchProductsCatalogue.type),
    debounceTime(250),
    switchMap(() => {
      return getProductsCatalogueApi().pipe(
        map(catalogue => new FetchProductsCatalogueSuccess({catalogue})),
        buildCatchError(
          FetchProductsCatalogueError,
          'No se pudo obtener el catálogo'
        )
      );
    })
  );
}

export function fetchProductVariantOptionsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchProductVariantOptions.type),
    debounceTime(250),
    switchMap(() => {
      return getProductVariantOptionsApi().pipe(
        map(data => new FetchProductVariantOptionsSuccess({data})),
        buildCatchError(
          FetchProductVariantOptionsError,
          'Error al obtener variantes de producto'
        )
      );
    })
  );
}
